// src/components/store/sections/shop/BrandShopsSection.tsx
"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { 
  Store, 
  ArrowUpRight, 
  Loader2, 
  ArrowRight, 
  ChevronLeft, 
  ChevronRight 
} from "lucide-react";

export interface ShopItem {
  id: number | string;
  name: string;
  slug?: string;
  logo?: string | null;
  logo_url?: string | null;
  banner?: string | null;
  banner_url?: string | null;
  category_label?: string;
  description?: string;
}

interface BrandShopsSectionProps {
  subdomain?: string;
  initialShops?: ShopItem[];
  primaryColor?: string;
  layoutMode?: "1_row" | "2_rows" | "grid";
}

const SHOP_CARD_THEMES = [
  {
    gradient: "from-emerald-500 to-teal-700",
    waveFill: "#10b981",
    tagText: "SPECIAL OFFER",
    badge: "50% OFF",
    btnBorder: "border-emerald-200",
  },
  {
    gradient: "from-purple-600 to-indigo-800",
    waveFill: "#8b5cf6",
    tagText: "EXCLUSIVE BRAND",
    badge: "BIG SALE",
    btnBorder: "border-purple-200",
  },
  {
    gradient: "from-amber-500 to-orange-600",
    waveFill: "#f97316",
    tagText: "SUPER DEALS",
    badge: "50% OFF",
    btnBorder: "border-orange-200",
  },
  {
    gradient: "from-blue-500 to-cyan-600",
    waveFill: "#06b6d4",
    tagText: "TOP OUTLET",
    badge: "NEW ARRIVAL",
    btnBorder: "border-cyan-200",
  },
];

export default function BrandShopsSection({
  subdomain = "nextlook",
  initialShops = [],
  primaryColor = "#059669",
  layoutMode: initialLayoutMode,
}: BrandShopsSectionProps) {
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [shops, setShops] = useState<ShopItem[]>(initialShops);
  const [loading, setLoading] = useState<boolean>(initialShops.length === 0);
  
  // 🌟 অ্যাডমিন কন্ট্রোল অনুযায়ী রো লেআউট স্টেট (1_row | 2_rows | grid)
  const [activeLayout, setActiveLayout] = useState<"1_row" | "2_rows" | "grid">(
    initialLayoutMode || "1_row"
  );

  const getSubdomain = useCallback(() => {
    if (subdomain && subdomain.trim()) return subdomain.trim();

    if (typeof window !== "undefined") {
      const hostname = window.location.hostname.toLowerCase();
      if (hostname.includes(".")) {
        const parts = hostname.split(".");
        if (parts[0] !== "localhost" && parts[0] !== "127" && parts[0] !== "www") {
          return parts[0];
        }
      }
    }
    return "nextlook";
  }, [subdomain]);

  // শপ এবং অ্যাডমিন সেটিংস ফেচ করা
  useEffect(() => {
    async function fetchShopsAndSettings() {
      setLoading(true);
      const currentSub = getSubdomain();
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${currentSub}.localhost:8000` : "";

        // ১. শপ ডাটা ফেচ
        const resShops = await fetch(
          `${apiBase}/api/v1/stores/shops/?subdomain=${encodeURIComponent(currentSub)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Subdomain": currentSub,
            },
          }
        );

        if (resShops.ok) {
          const data = await resShops.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.shops || [];
          setShops(list);
        }

        // ২. অ্যাডমিন থেকে সেট করা রো লেআউট সেটিংস ফেচ
        const resSettings = await fetch(
          `${apiBase}/api/v1/stores/settings/?subdomain=${encodeURIComponent(currentSub)}`
        );
        if (resSettings.ok) {
          const sData = await resSettings.json();
          const savedLayout =
            sData?.section_designs?.brand_shops_layout ||
            sData?.theme_config?.brand_shops_layout ||
            (typeof window !== "undefined" ? localStorage.getItem("brand_shops_layout") : null);

          if (savedLayout && ["1_row", "2_rows", "grid"].includes(savedLayout)) {
            setActiveLayout(savedLayout as any);
          }
        }
      } catch (err) {
        console.error("Error fetching shops or settings:", err);
      } finally {
        setLoading(false);
      }
    }

    if (initialShops && initialShops.length > 0) {
      setShops(initialShops);
      setLoading(false);
      // লোকালস্টোরেজ থেকে ব্যাকআপ লেআউট চেক
      if (typeof window !== "undefined") {
        const local = localStorage.getItem("brand_shops_layout");
        if (local && ["1_row", "2_rows", "grid"].includes(local)) {
          setActiveLayout(local as any);
        }
      }
    } else {
      fetchShopsAndSettings();
    }
  }, [initialShops, getSubdomain]);

  // হরাইজন্টাল স্ক্রোল বাটন হ্যান্ডলার
  const handleScroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleShopClick = (shopId: string | number) => {
    router.push(`/products?shop=${shopId}`);
  };

  const formatImg = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  if (!loading && shops.length === 0) {
    return null;
  }

  return (
    <section className="w-full py-8 sm:py-12 bg-slate-50/50 select-none font-sans">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
        
        {/* সেকশন হেডার */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 border-b border-slate-200/80 pb-4">
          <div className="space-y-1 text-left">
            <div className="flex items-center gap-2">
              <span 
                style={{ color: primaryColor }}
                className="text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-1"
              >
                <Store className="w-3.5 h-3.5" />
                <span>Featured Brand Shops</span>
              </span>
              <span className="text-[9px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
                Live Outlets
              </span>
            </div>

            <h2 className="text-xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Explore Our Brand Shops & Outlets
            </h2>
            <p className="text-xs text-slate-500">
              নির্দিষ্ট দোকানে ক্লিক করে তার সম্পূর্ণ প্রোডাক্ট ক্যাটালগ ও এক্সক্লুসিভ অফার দেখে নিন।
            </p>
          </div>

          <div className="flex items-center gap-3 self-start sm:self-auto">
            {/* হরাইজন্টাল স্ক্রোল বাটনসমূহ (যদি 1_row বা 2_rows সক্রিয় থাকে) */}
            {activeLayout !== "grid" && shops.length > 3 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleScroll("left")}
                  className="w-8 h-8 rounded-full border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95"
                  aria-label="Previous Shops"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleScroll("right")}
                  className="w-8 h-8 rounded-full border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95"
                  aria-label="Next Shops"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => router.push("/products")}
              className="text-xs font-black text-slate-700 hover:text-emerald-600 flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
            >
              <span>View All</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* লোডার */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">
              দোকানের তালিকা লোড হচ্ছে...
            </span>
          </div>
        ) : (
          /* 🌟 শপ কার্ড কন্টেইনার (লেআউট কন্ট্রোল অনুযায়ী রেন্ডার হবে) */
          <div
            ref={scrollRef}
            className={`transition-all duration-300 ${
              activeLayout === "1_row"
                ? "flex gap-5 overflow-x-auto scrollbar-none snap-x snap-mandatory py-2 px-1"
                : activeLayout === "2_rows"
                ? "grid grid-rows-2 grid-flow-col gap-5 overflow-x-auto scrollbar-none snap-x snap-mandatory py-2 px-1"
                : "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6"
            }`}
          >
            {shops.map((shop, index) => {
              const theme = SHOP_CARD_THEMES[index % SHOP_CARD_THEMES.length];
              const logoUrl = formatImg(shop.logo_url || shop.logo || shop.banner_url || shop.banner);

              return (
                <div
                  key={shop.id}
                  onClick={() => handleShopClick(shop.id)}
                  className={`group relative h-[400px] rounded-3xl bg-white overflow-hidden shadow-md hover:shadow-2xl border border-slate-200/90 cursor-pointer flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 select-none shrink-0 ${
                    activeLayout !== "grid"
                      ? "w-[290px] sm:w-[315px] snap-start"
                      : "w-full"
                  }`}
                >
                  {/* 🌟 পুরো সাদা অংশ জুড়ে শপের ছবি (Full White Part Cover) */}
                  <div className="relative w-full h-[225px] bg-white overflow-hidden flex flex-col justify-between p-3.5 z-10 border-b border-slate-100">
                    
                    {/* ব্যাকগ্রাউন্ড সম্পূর্ণ কভার ছবি */}
                    <div className="absolute inset-0 w-full h-full bg-slate-50">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={shop.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-100">
                          <Store className="w-16 h-16 text-slate-300" />
                        </div>
                      )}

                      {/* টেক্সট স্পষ্ট দেখার জন্য সফট গ্রেডিয়েন্ট ওভারলে */}
                      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-black/45 pointer-events-none" />
                    </div>

                    {/* টপ কার্ড হেডার বক্স (ছবিটির ওপর ভেসে থাকবে) */}
                    <div className="relative z-10 border border-white/80 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-xl shadow-md w-full flex flex-col items-center justify-center">
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 block mb-0.5 truncate max-w-full">
                        {theme.tagText}
                      </span>
                      <h4 className="text-base font-black text-slate-950 uppercase tracking-tight leading-tight line-clamp-1">
                        {shop.name}
                      </h4>
                    </div>

                    {/* বটম ক্যাটাগরি লেবেল ব্যাজ */}
                    <div className="relative z-10 self-center">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-800 bg-white/95 backdrop-blur-md px-3.5 py-1 rounded-full shadow-md border border-slate-200 line-clamp-1">
                        {shop.category_label || "Official Shop"}
                      </span>
                    </div>
                  </div>

                  {/* বটম ভাইব্র্যান্ট ফ্লুইড ওয়েভ সেকশন */}
                  <div
                    className={`relative w-full h-44 bg-gradient-to-br ${theme.gradient} flex flex-col items-center justify-end pb-5 px-4 overflow-hidden`}
                  >
                    <svg
                      className="absolute -top-1 left-0 w-full h-16 text-white preserve-3d"
                      viewBox="0 0 500 150"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M0,0 C150,90 350,-40 500,60 L500,0 L0,0 Z"
                        fill="white"
                      />
                    </svg>

                    <div
                      className="absolute inset-0 opacity-25"
                      style={{
                        background: `radial-gradient(circle at 50% 120%, ${theme.waveFill}, transparent 70%)`,
                      }}
                    />

                    <div className="relative z-10 text-center mb-2.5">
                      <span className="text-3xl font-black text-white tracking-tight drop-shadow-md block leading-none">
                        {theme.badge}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-white/90">
                        CLICK TO OPEN
                      </span>
                    </div>

                    <div className="relative z-10 w-full">
                      <div
                        className={`w-full py-1.5 rounded-full border ${theme.btnBorder} bg-white/20 hover:bg-white/30 backdrop-blur-xs flex items-center justify-center gap-1 text-[11px] font-black tracking-widest text-white uppercase shadow-sm transition-transform group-hover:scale-102`}
                      >
                        <span>VISIT SHOP</span>
                        <ArrowUpRight className="w-3.5 h-3.5 stroke-[2.5]" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}